/**
 * Notification Navigation Utilities
 * 
 * Provides comprehensive, actionable navigation for different notification types.
 * Each notification type gets specific routing logic to take users directly to the relevant item.
 */

import { NOTIFICATION_TYPES, type Notification } from '@/types/notification';
import { typedSupabase } from '@/lib/typed-supabase';
import { logger } from '@/lib/logger';

/**
 * Validate that a URL is safe for external navigation (prevents open redirect attacks).
 * Only allows https:// URLs. Rejects javascript:, data:, and other dangerous protocols.
 */
function isSafeExternalUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Validate that a route string is a safe internal path (starts with /).
 * Prevents protocol-relative URLs (//evil.com) and absolute URL injection.
 */
function isSafeInternalRoute(route: string): boolean {
  return route.startsWith('/') && !route.startsWith('//');
}

export interface NavigationAction {
  /** The URL to navigate to */
  url: string;
  /** Display text for the action button */
  label: string;
  /** Whether to open in a new tab */
  external?: boolean;
  /** Icon name for the action */
  icon?: string;
  /** Description of what this action does */
  description?: string;
}

export interface NotificationActions {
  /** Primary action - the main action when clicking the notification */
  primary?: NavigationAction;
  /** Secondary actions - additional related actions */
  secondary?: NavigationAction[];
}

/**
 * Generate comprehensive navigation actions for a notification
 */
export function getNotificationActions(notification: Notification): NotificationActions {
  const { type, entity_type, entity_id, data } = notification;
  const notificationData = data as Record<string, unknown> | null;
  
  // Normalize type to uppercase for comparison, but handle specific known lowercase types
  const normalizedType = type?.toUpperCase() || '';

  // Primary action - main navigation
  const primary = getPrimaryAction(normalizedType, entity_type, entity_id, notificationData);
  
  // Secondary actions - additional related actions
  const secondary = getSecondaryActions(normalizedType, entity_type, entity_id, notificationData);

  return { primary, secondary };
}

/**
 * Get the primary action for a notification
 */
function getPrimaryAction(
  type: string,
  entityType: string | null,
  entityId: string | null,
  data: Record<string, unknown> | null
): NavigationAction | undefined {
  
  // Task-related notifications
  if ((type === 'TASK_ASSIGNED' || type === 'TASK_UNASSIGNMENT') && entityId) {
    return {
      url: `/admin/tasks?openTask=${entityId}`,
      label: type === 'TASK_ASSIGNED' ? 'View Task' : 'View Task Details',
      icon: 'ExternalLink',
      description: 'Open the task'
    };
  }

  if (type === 'TASK_COMPLETED' && entityId) {
    return {
      url: `/admin/tasks?openTask=${entityId}`,
      label: 'View Completed Task',
      icon: 'CheckCircle',
      description: 'Review the completed task'
    };
  }

  if (type === 'TASK_STATUS_CHANGED' && entityId) {
    return {
      url: `/admin/tasks?openTask=${entityId}`,
      label: 'View Task Status',
      icon: 'RefreshCw',
      description: 'Check the updated task status'
    };
  }

  if (type === 'TASK_DUE_REMINDER' && entityId) {
    return {
      url: `/admin/tasks?openTask=${entityId}`,
      label: 'Complete Task',
      icon: 'Clock',
      description: 'Task is due soon - complete it now'
    };
  }

  if (type === 'TASK_OVERDUE' && entityId) {
    return {
      url: `/admin/tasks?openTask=${entityId}`,
      label: 'Complete Overdue Task',
      icon: 'AlertTriangle',
      description: 'This task is overdue and needs attention'
    };
  }

  if (type === 'TASK_COMMENT' && entityId) {
    return {
      url: `/admin/tasks?openTask=${entityId}&tab=comments`,
      label: 'View Comment',
      icon: 'MessageSquare',
      description: 'Read the new comment on your task'
    };
  }

  if (type === 'TASK_PRIORITY_CHANGED' && entityId) {
    return {
      url: `/admin/tasks?openTask=${entityId}`,
      label: 'Check Priority',
      icon: 'ArrowUp',
      description: 'View the updated task priority'
    };
  }

  if (type === 'TASK_ATTACHMENT_ADDED' && entityId) {
    return {
      url: `/admin/tasks?openTask=${entityId}&tab=attachments`,
      label: 'View Attachment',
      icon: 'Paperclip',
      description: 'Check the new file attachment'
    };
  }

  if (type === 'SUBTASK_COMPLETED' && entityId) {
    return {
      url: `/admin/tasks?openTask=${entityId}&tab=subtasks`,
      label: 'View Subtasks',
      icon: 'List',
      description: 'Check subtask completion progress'
    };
  }

  // CRM notifications
  if (type === 'CRM_CLIENT_CREATED' && entityId) {
    return {
      url: `/admin/clients?client=${entityId}`,
      label: 'View Client',
      icon: 'UserPlus',
      description: 'View the newly created client'
    };
  }

  if (type === 'CRM_CLIENT_STATUS_CHANGED' && entityId) {
    return {
      url: `/admin/clients?client=${entityId}`,
      label: 'View Client Status',
      icon: 'RefreshCw',
      description: 'Check updated client status'
    };
  }

  if (type === 'CRM_DEAL_STAGE_CHANGED' && entityId) {
    return {
      url: `/admin/sales-portal?deal=${entityId}`,
      label: 'View Deal',
      icon: 'TrendingUp',
      description: 'Check the updated deal stage'
    };
  }

  // Mention notifications with context-aware routing
  if (type === 'TASK_MENTION' || type === 'MENTION' || type === 'COMMUNITY_MENTION') {
    if (entityType === 'task' && entityId) {
      return {
        url: `/admin/tasks?openTask=${entityId}&highlight=mention`,
        label: 'View Mention',
        icon: 'AtSign',
        description: 'You were mentioned in this task'
      };
    }
    if (entityType === 'task_comment' && data?.task_id) {
      return {
        url: `/admin/tasks?openTask=${data.task_id}&tab=comments&highlight=mention`,
        label: 'View Mention',
        icon: 'AtSign',
        description: 'You were mentioned in a task comment'
      };
    }
    if (entityType === 'post' && entityId) {
      return {
        url: `/community?open=${entityId}&highlight=mention`,
        label: 'View Mention',
        icon: 'AtSign',
        description: 'You were mentioned in a community post'
      };
    }
    if (entityType === 'comment' && entityId) {
      return {
        url: `/community?open=${entityId}&highlight=mention`,
        label: 'View Mention',
        icon: 'AtSign',
        description: 'You were mentioned in a comment'
      };
    }
  }

  // Community notifications
  if (type === NOTIFICATION_TYPES.POST_LIKED && entityType === 'post' && entityId) {
    return {
      url: `/community?open=${entityId}`,
      label: 'View Post',
      icon: 'Heart',
      description: 'See who liked your post'
    };
  }

  if (type === NOTIFICATION_TYPES.POST_COMMENTED && entityType === 'post' && entityId) {
    return {
      url: `/community?open=${entityId}&tab=comments`,
      label: 'View Comment',
      icon: 'MessageSquare',
      description: 'Read the new comment on your post'
    };
  }

  if (type === NOTIFICATION_TYPES.COMMENT_REPLIED && entityType === 'comment' && entityId) {
    return {
      url: `/community?open=${entityId}&highlight=reply`,
      label: 'View Reply',
      icon: 'Reply',
      description: 'Someone replied to your comment'
    };
  }

  if (type === NOTIFICATION_TYPES.COMMUNITY_MENTION && entityType === 'post' && entityId) {
    return {
      url: `/community?open=${entityId}&highlight=mention`,
      label: 'View Mention',
      icon: 'AtSign',
      description: 'You were mentioned in a community post'
    };
  }

  // Achievement notifications
  if (type === NOTIFICATION_TYPES.ACHIEVEMENT_BADGE) {
    return {
      url: '/achievements',
      label: 'View Achievement',
      icon: 'Trophy',
      description: 'Check out your new achievement badge'
    };
  }

  if (type === NOTIFICATION_TYPES.XP_MILESTONE || type === NOTIFICATION_TYPES.RIZZ_MILESTONE) {
    return {
      url: '/profile?tab=stats',
      label: 'View Milestone',
      icon: 'Star',
      description: 'Celebrate your XP milestone'
    };
  }

  if (type === NOTIFICATION_TYPES.STREAK_BONUS) {
    return {
      url: '/profile?tab=streaks',
      label: 'View Streak',
      icon: 'Zap',
      description: 'Check your streak bonus progress'
    };
  }

  if (type === NOTIFICATION_TYPES.LEADERBOARD_UPDATE) {
    return {
      url: '/admin/leaderboards',
      label: 'View Leaderboard',
      icon: 'Crown',
      description: 'See your new leaderboard position'
    };
  }

  // System notifications
  if (type === NOTIFICATION_TYPES.SYSTEM_ANNOUNCEMENT) {
    if (data?.url && isSafeExternalUrl(data.url as string)) {
      return {
        url: data.url as string,
        label: 'Read Announcement',
        icon: 'Megaphone',
        description: 'View the system announcement',
        external: true
      };
    }
    if (data?.route && isSafeInternalRoute(data.route as string)) {
      return {
        url: data.route as string,
        label: 'View Details',
        icon: 'Info',
        description: 'Get more information'
      };
    }
  }

  if (type === NOTIFICATION_TYPES.ADMIN_ACTION) {
    if (data?.url && isSafeExternalUrl(data.url as string)) {
      return {
        url: data.url as string,
        label: 'Take Action',
        icon: 'Settings',
        description: 'Perform the required admin action',
        external: data?.external as boolean
      };
    }
    if (data?.route && isSafeInternalRoute(data.route as string)) {
      return {
        url: data.route as string,
        label: 'Go to Admin',
        icon: 'Shield',
        description: 'Access the admin interface'
      };
    }
  }

  if (type === NOTIFICATION_TYPES.ROLE_CHANGED) {
    return {
      url: '/profile?tab=permissions',
      label: 'View Permissions',
      icon: 'UserCheck',
      description: 'Check your updated role permissions'
    };
  }

  // Fallback for unknown notification types
  return {
    url: '/notifications',
    label: 'View Details',
    icon: 'ExternalLink',
    description: 'Get more information about this notification'
  };
}

/**
 * Get secondary actions for a notification
 */
function getSecondaryActions(
  type: string,
  entityType: string | null,
  entityId: string | null,
  data: Record<string, unknown> | null
): NavigationAction[] {
  const actions: NavigationAction[] = [];

  // Task-related secondary actions
  if (entityType === 'task' && entityId) {
    // Add "View All Tasks" as a secondary action
    actions.push({
      url: '/admin/tasks',
      label: 'All Tasks',
      icon: 'List',
      description: 'View all your tasks'
    });

    // For task assignments, add client context if available
    if (type === NOTIFICATION_TYPES.TASK_ASSIGNED && data?.client_id) {
      actions.push({
        url: `/admin/clients?client=${data.client_id}`,
        label: 'View Client',
        icon: 'Building',
        description: 'View client details'
      });
    }

    // For task comments, add profile of commenter
    if (type === NOTIFICATION_TYPES.TASK_COMMENT && data?.commenter_id) {
      actions.push({
        url: `/profile?user=${data.commenter_id}`,
        label: 'View Profile',
        icon: 'User',
        description: 'View commenter profile'
      });
    }
  }

  // Community secondary actions
  if (entityType === 'post' || entityType === 'comment') {
    actions.push({
      url: '/community',
      label: 'Browse Community',
      icon: 'Users',
      description: 'Explore more community posts'
    });

    // Add author profile link if available
    if (data?.author_id) {
      actions.push({
        url: `/profile?user=${data.author_id}`,
        label: 'View Profile',
        icon: 'User',
        description: 'View author profile'
      });
    }
  }

  // Achievement secondary actions
  if (type === NOTIFICATION_TYPES.ACHIEVEMENT_BADGE || 
      type === NOTIFICATION_TYPES.XP_MILESTONE ||
      type === NOTIFICATION_TYPES.RIZZ_MILESTONE || 
      type === NOTIFICATION_TYPES.STREAK_BONUS) {
    actions.push({
      url: '/achievements',
      label: 'All Achievements',
      icon: 'Trophy',
      description: 'View all your achievements'
    });

    actions.push({
      url: '/admin/leaderboards',
      label: 'Leaderboard',
      icon: 'Crown',
      description: 'Check your ranking'
    });
  }

  // System notification secondary actions
  if (type === NOTIFICATION_TYPES.SYSTEM_ANNOUNCEMENT || type === NOTIFICATION_TYPES.ADMIN_ACTION) {
    actions.push({
      url: '/admin/dashboard',
      label: 'Admin Dashboard',
      icon: 'LayoutGrid',
      description: 'Access admin controls'
    });
  }

  // Always add notification settings as a secondary action
  actions.push({
    url: '/profile?tab=notifications',
    label: 'Notification Settings',
    icon: 'Settings',
    description: 'Manage notification preferences'
  });

  return actions;
}

/**
 * Get a direct deep link URL for a notification
 */
export function getNotificationDeepLink(notification: Notification): string {
  const actions = getNotificationActions(notification);
  return actions.primary?.url || '/notifications';
}

/**
 * Check if a notification has actionable content
 */
export function isActionableNotification(notification: Notification): boolean {
  const actions = getNotificationActions(notification);
  return !!(actions.primary ?? actions.secondary?.length);
}

/**
 * Get notification urgency level for prioritization
 */
export function getNotificationUrgency(notification: Notification): 'low' | 'medium' | 'high' | 'critical' {
  const { type } = notification;
  const upperType = type?.toUpperCase() || '';

  // Critical notifications
  if (upperType === 'TASK_OVERDUE') {
    return 'critical';
  }

  // High priority notifications
  if ([
    'TASK_DUE_REMINDER',
    'ADMIN_ACTION',
    'ROLE_CHANGED',
    'CRM_FOLLOWUP_DUE'
  ].includes(upperType)) {
    return 'high';
  }

  // Medium priority notifications
  if ([
    'TASK_ASSIGNED',
    'TASK_MENTION',
    'MENTION',
    'TASK_PRIORITY_CHANGED',
    'SYSTEM_ANNOUNCEMENT',
    'CRM_CLIENT_CREATED',
    'CRM_CLIENT_ASSIGNED',
    'CRM_DEAL_STAGE_CHANGED'
  ].includes(upperType)) {
    return 'medium';
  }

  // Low priority notifications (achievements, likes, etc.)
  return 'low';
}

export const NOTIFICATION_TOAST_WINDOW_MS = 5000;

export function getMostRecentUnreadNotification(
  notifications: Notification[],
  now = Date.now(),
  windowMs = NOTIFICATION_TOAST_WINDOW_MS
): Notification | null {
  const oldestTimestamp = now - windowMs;
  const recentUnread = notifications
    .filter((notification) => !notification.is_read)
    .filter((notification) => new Date(notification.created_at).getTime() > oldestTimestamp)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return recentUnread[0] ?? null;
}

export function getNotificationUrgencyEmoji(
  urgency: ReturnType<typeof getNotificationUrgency>
): string {
  switch (urgency) {
    case 'critical':
      return '🔴';
    case 'high':
      return '🟡';
    case 'medium':
      return '🔵';
    default:
      return '';
  }
}

export function getNotificationToastDuration(
  urgency: ReturnType<typeof getNotificationUrgency>
): number {
  switch (urgency) {
    case 'critical':
      return 10000;
    case 'high':
      return 8000;
    case 'medium':
      return 6000;
    default:
      return 4000;
  }
}

// ============================================================================
// ORGANIZATION CONTEXT RESOLUTION
// ============================================================================

/**
 * Map of entity_type values to their database table and whether they have an organization_id column.
 * Only org-scoped entity types are included here.
 */
const ENTITY_TABLE_MAP: Record<string, string> = {
  client: 'clients',
  task: 'todos',
  todo: 'todos',
  opportunity: 'opportunities',
  project: 'projects',
  sales_transaction: 'sales_transactions',
  sales_team: 'sales_teams',
};

/**
 * Resolve the organization_id for the entity referenced by a notification.
 *
 * This performs a lightweight single-column SELECT to look up which organization
 * owns the entity (client, task, opportunity, etc.) so the UI can switch org
 * context before navigating.
 *
 * @returns The organization_id string if found, or null if the entity type is
 *          not org-scoped or the lookup fails.
 */
export async function resolveNotificationOrganization(
  notification: Notification
): Promise<string | null> {
  const { entity_type, entity_id } = notification;

  // No entity to look up
  if (!entity_type || !entity_id) {
    return null;
  }

  // Normalise to lowercase for the map lookup
  const normalizedType = entity_type.toLowerCase();
  const tableName = ENTITY_TABLE_MAP[normalizedType];

  if (!tableName) {
    // Entity type is not org-scoped (e.g. post, comment, achievement)
    return null;
  }

  try {
    const { data, error } = await (
      typedSupabase as unknown as {
        from: (table: string) => {
          select: (columns: string) => {
            eq: (column: string, value: string) => {
              maybeSingle: () => Promise<{
                data: { organization_id: string } | null;
                error: { message: string } | null;
              }>;
            };
          };
        };
      }
    )
      .from(tableName)
      .select('organization_id')
      .eq('id', entity_id)
      .maybeSingle();

    if (error) {
      logger.warn('Failed to resolve notification organization', {
        entity_type,
        entity_id,
        tableName,
        error: error.message,
      });
      return null;
    }

    return data?.organization_id ?? null;
  } catch (err) {
    logger.error('Unexpected error resolving notification organization', {
      entity_type,
      entity_id,
      error: err,
    });
    return null;
  }
}

